<template>
  <div class="hp-root" :class="{ 'dark-mode': isDarkMode }">
    <!-- Pas 1.2: watermark peste editorul demo fără cont -->
    <div v-if="editor.isDemoMode?.value" class="demo-banner" role="note">
      <span>{{ $t('hp.demo.banner') }}</span>
      <button type="button" class="demo-banner-cta" @click="signupFromDemo">
        {{ $t('hp.demo.signup') }} <i class="fas fa-arrow-right"></i>
      </button>
    </div>
    <SrtEditor v-if="editor.showSrtEditor.value || editor.showTextEditor?.value" :editor="editor"/>

    <div v-if="!editor.showSrtEditor.value">
      <!-- ============ MARKETING HOMEPAGE — always reachable, even after login.
           Authenticated users land on /Users/dashboard instead (see
           postLoginRedirect), and the CTAs below adapt to the session. ============ -->
      <div>
        <!-- productionradio keeps its minimal login gate -->
        <div v-if="currentMode === 'productionradio'" class="radio-gate">
          <h1 class="radio-title">{{ $t('Radio Romania') }}</h1>
          <p class="radio-sub">{{ $t('Bine ati venit, pentru a continua va rugam sa va logati') }}</p>
          <button @click.stop="$router.push('/login')" class="hp-btn hp-btn-primary">
            {{ $t('Aici') }} <i class="fas fa-arrow-right"></i>
          </button>
        </div>

        <template v-else>
          <!-- ===== OPENING: LIVE TRANSCRIPTION CONSOLE ===== -->
          <section class="console-section">
            <div class="console-grid">
              <!-- Left: message -->
              <div class="console-copy">
                <div class="live-chip">
                  <span class="live-dot"></span>
                  <span class="live-mono">{{ $t('hp.live') }}</span>
                </div>
                <h1 class="console-headline">
                  {{ $t('hp.headline.a') }}
                  <span class="headline-accent">{{ $t('hp.headline.b') }}</span>
                </h1>
                <p class="console-sub">{{ $t('hp.sub') }}</p>
                <div class="console-ctas">
                  <router-link v-if="!authStore.isAuthenticated" to="/login" class="hp-btn hp-btn-primary hp-btn-lg" @click="trackSignupStarted('hero')">
                    <i class="fas fa-bolt"></i> {{ $t('hp.cta.start') }}
                  </router-link>
                  <router-link v-else :to="dashboardPath" class="hp-btn hp-btn-primary hp-btn-lg">
                    <i class="fas fa-gauge-high"></i> {{ $t('hp.cta.dashboard') }}
                  </router-link>
                  <button @click.stop="openDemoInEditor" class="hp-btn hp-btn-ghost hp-btn-lg" :disabled="demoOpening">
                    <i class="fas fa-play-circle"></i> {{ $t('hp.cta.demo') }}
                  </button>
                </div>
                <!-- Two doors: Everyday (camera-first) vs Pro (SRT Editor) -->
                <div class="two-doors" role="group" :aria-label="$t('hp.doors.label')">
                  <router-link :to="everydayPath" class="door-card door-everyday">
                    <span class="door-icon"><i class="fas fa-camera"></i></span>
                    <span class="door-body">
                      <span class="door-title">{{ $t('hp.doors.everyday.title') }}</span>
                      <span class="door-desc">{{ $t('hp.doors.everyday.desc') }}</span>
                    </span>
                    <span class="door-arrow" aria-hidden="true"><i class="fas fa-arrow-right"></i></span>
                  </router-link>
                  <router-link :to="proPath" class="door-card door-pro">
                    <span class="door-icon"><i class="fas fa-closed-captioning"></i></span>
                    <span class="door-body">
                      <span class="door-title">{{ $t('hp.doors.pro.title') }}</span>
                      <span class="door-desc">{{ $t('hp.doors.pro.desc') }}</span>
                    </span>
                    <span class="door-arrow" aria-hidden="true"><i class="fas fa-arrow-right"></i></span>
                  </router-link>
                </div>
                <div class="console-meta mono">
                  <span>{{ $t('hp.meta.formats') }}</span>
                </div>
              </div>

              <!-- Right: the transcription console -->
              <div class="console-panel">
                <div class="console-window">
                  <div class="console-titlebar">
                    <span class="tb-dot red"></span><span class="tb-dot amber"></span><span class="tb-dot green"></span>
                    <span class="tb-file mono">demo.mp3</span>
                    <span class="tb-rec mono"><span class="rec-dot"></span>DEMO</span>
                  </div>

                  <!-- Pas 1.2: redare reală a assetului demo (fără typing fals).
                       Cue-ul afișat e sincronizat cu audio din același SRT
                       pe care îl deschide editorul. -->
                  <audio
                    ref="demoAudio"
                    :src="demoAudioUrl"
                    preload="metadata"
                    @timeupdate="onDemoTimeUpdate"
                    @loadedmetadata="onDemoLoadedMetadata"
                    @play="isPlaying = true"
                    @pause="isPlaying = false"
                    @ended="onDemoEnded"
                  ></audio>

                  <!-- Waveform -->
                  <div class="waveform" @click="togglePlay" role="button" :aria-label="isPlaying ? $t('hp.demo.pause') : $t('hp.demo.play')">
                    <span
                      v-for="(bar, i) in waveBars"
                      :key="i"
                      class="wave-bar"
                      :class="{ playing: isPlaying }"
                      :style="{ height: bar + '%', animationDelay: (i * 0.045) + 's' }"
                    ></span>
                    <div class="wave-progress" :style="{ width: playhead + '%' }"></div>
                  </div>
                  <div class="timecode-row mono">
                    <span>{{ formatTime(playheadSeconds) }}</span>
                    <span class="tc-lang">RO</span>
                    <span>{{ formatTime(demoDurationSeconds).slice(3) }}</span>
                  </div>

                  <!-- Synced caption from the real demo transcript -->
                  <div class="caption-box">
                    <div class="caption-label mono">{{ $t('hp.caption.label') }}</div>
                    <p class="caption-text">
                      {{ demoLoadFailed ? $t('hp.demo.loadError') : (activeCueText || '…') }}
                    </p>
                  </div>

                  <!-- Demo controls -->
                  <div class="lang-chips">
                    <button type="button" class="lang-chip mono" @click="togglePlay">
                      <i :class="isPlaying ? 'fas fa-pause' : 'fas fa-play'"></i>
                      {{ isPlaying ? $t('hp.demo.pause') : $t('hp.demo.play') }}
                    </button>
                    <button type="button" class="lang-chip mono" @click="openDemoInEditor" :disabled="demoOpening">
                      <i class="fas fa-closed-captioning"></i> {{ $t('hp.demo.openEditor') }}
                    </button>
                  </div>
                  <p class="demo-asset-note mono">{{ $t('hp.demo.assetNote') }}</p>
                </div>
              </div>
            </div>
          </section>

          <!-- ===== LANGUAGE MARQUEE ===== -->
          <section class="marquee-section">
            <div class="marquee-track">
              <span v-for="(lang, i) in marqueeLangs" :key="i" class="marquee-item">
                <span class="marquee-name">{{ lang.name }}</span>
                <span class="marquee-code mono">{{ lang.code }}</span>
              </span>
            </div>
          </section>

          <!-- ===== HOW IT WORKS: OFFSET TIMELINE ===== -->
          <section class="steps-section">
            <div class="section-head">
              <span class="section-kicker mono">{{ $t('hp.steps.kicker') }}</span>
              <h2 class="section-title">{{ $t('hp.steps.title') }}</h2>
            </div>
            <div class="steps-timeline">
              <div
                v-for="(step, i) in steps"
                :key="i"
                class="step-row"
                :class="{ 'step-flip': i % 2 === 1 }"
              >
                <div class="step-num mono">0{{ i + 1 }}</div>
                <div class="step-body">
                  <div class="step-icon"><i :class="step.icon"></i></div>
                  <h3 class="step-title">{{ $t(step.title) }}</h3>
                  <p class="step-desc">{{ $t(step.desc) }}</p>
                </div>
              </div>
            </div>
          </section>

          <!-- ===== STATS: BIG NUMBERS ===== -->
          <section class="stats-section">
            <div class="stats-grid">
              <div v-for="stat in stats" :key="stat.label" class="stat-block">
                <div class="stat-number">
                  <span class="stat-value">{{ stat.animated }}</span><span class="stat-suffix">{{ stat.suffix }}</span>
                </div>
                <div class="stat-label">{{ $t(stat.label) }}</div>
              </div>
            </div>
          </section>

          <!-- ===== CAPABILITIES: EDITORIAL SPLIT ===== -->
          <section class="caps-section">
            <div class="section-head">
              <span class="section-kicker mono">{{ $t('hp.caps.kicker') }}</span>
              <h2 class="section-title">{{ $t('hp.caps.title') }}</h2>
            </div>
            <div class="caps-grid">
              <div class="cap-card cap-wide">
                <div class="cap-icon"><i class="fas fa-closed-captioning"></i></div>
                <h3>{{ $t('hp.caps.editor.title') }}</h3>
                <p>{{ $t('hp.caps.editor.desc') }}</p>
                <div class="cap-tags mono">
                  <span>SRT</span><span>VTT</span><span>ASS</span><span>DOCX</span>
                </div>
              </div>
              <div class="cap-card">
                <div class="cap-icon"><i class="fas fa-magic"></i></div>
                <h3>{{ $t('hp.caps.ai.title') }}</h3>
                <p>{{ $t('hp.caps.ai.desc') }}</p>
              </div>
              <div class="cap-card">
                <div class="cap-icon"><i class="fas fa-users"></i></div>
                <h3>{{ $t('hp.caps.human.title') }}</h3>
                <p>{{ $t('hp.caps.human.desc') }}</p>
              </div>
              <div class="cap-card">
                <div class="cap-icon"><i class="fas fa-language"></i></div>
                <h3>{{ $t('hp.caps.translate.title') }}</h3>
                <p>{{ $t('hp.caps.translate.desc') }}</p>
              </div>
              <div class="cap-card">
                <div class="cap-icon"><i class="fas fa-clapperboard"></i></div>
                <h3>{{ $t('hp.caps.shorts.title') }}</h3>
                <p>{{ $t('hp.caps.shorts.desc') }}</p>
                <div class="cap-tags mono">
                  <span>9:16</span><span>60s</span><span>AI</span>
                </div>
              </div>
              <div class="cap-card cap-wide">
                <div class="cap-icon"><i class="fas fa-film"></i></div>
                <h3>{{ $t('hp.caps.video.title') }}</h3>
                <p>{{ $t('hp.caps.video.desc') }}</p>
                <div class="cap-tags mono">
                  <span>MP4</span><span>1080p</span><span>{{ $t('hp.caps.video.burn') }}</span>
                </div>
              </div>
            </div>
          </section>

          <!-- ===== PRICING TEASER ===== -->
          <section class="pricing-teaser">
            <div class="teaser-inner">
              <div class="teaser-copy">
                <h2>{{ $t('hp.pricing.title') }}</h2>
                <p>{{ $t('hp.pricing.sub') }}</p>
              </div>
              <router-link to="/upgrade" class="hp-btn hp-btn-light hp-btn-lg">
                {{ $t('hp.pricing.cta') }} <i class="fas fa-arrow-right"></i>
              </router-link>
            </div>
          </section>

          <!-- ===== FOOTER CTA ===== -->
          <section class="footer-cta">
            <h2 class="footer-headline">{{ $t('hp.footer.title') }}</h2>
            <router-link v-if="!authStore.isAuthenticated" to="/login" class="hp-btn hp-btn-primary hp-btn-lg" @click="trackSignupStarted('footer')">
              <i class="fas fa-bolt"></i> {{ $t('hp.footer.cta') }}
            </router-link>
            <router-link v-else :to="dashboardPath" class="hp-btn hp-btn-primary hp-btn-lg">
              <i class="fas fa-gauge-high"></i> {{ $t('hp.cta.dashboard') }}
            </router-link>
            <p class="footer-note mono">{{ $t('hp.footer.note') }}</p>
            <p class="footer-guides">
              <router-link to="/docs" class="footer-guides-link">{{ $t('User guides') }}</router-link>
            </p>
          </section>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, defineAsyncComponent } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthUserStore } from '@stores/auth';
import { getPostLoginPath } from '@utils/postLoginRedirect';
import { useThemeStore } from '@stores/themeStore';
import { storeToRefs } from 'pinia';
import { trackEvent } from '@utils/analytics';
import { parseDemoSrt, activeDemoCue, DEMO_AUDIO_URL, DEMO_SRT_URL } from '@utils/demoTranscript';
import { useTranscriptEditor } from '@components/SrtEditor/useTranscriptEditor';
// Pas 1.1: the editor is the heaviest module in the app — landing
// visitors must not download it. It loads as the `editor` chunk only
// when openSrtEditor() flips showSrtEditor (template v-if above).
const SrtEditor = defineAsyncComponent(
  () => import(/* @viteChunkName: "editor" */ '@components/SrtEditor/SrtEditor.vue'),
);

export default {
  name: 'HomeContent',
  components: { SrtEditor },
  setup() {
    const authStore = useAuthUserStore();
    const themeStore = useThemeStore();
    const { isDarkMode } = storeToRefs(themeStore);
    const editor = useTranscriptEditor();
    const router = useRouter();
    const currentMode = import.meta.env.MODE;

    // Where the "Open dashboard" CTA points for the signed-in visitor.
    const dashboardPath = computed(() => getPostLoginPath(authStore.userGroups));
    // Two doors — query-driven mode: camera-first vs SRT Editor.
    const everydayPath = computed(() => ({ path: '/Users/transcriptordashboard', query: { mode: 'everyday' } }));
    const proPath = computed(() => ({ path: '/Users/transcriptordashboard', query: { mode: 'pro' } }));

    // ---- Demo real fără cont (Pas 1.2) ----
    // Asset TEMPORAR din UI/public/demo/ (exemplu tehnic sintetic — vezi
    // README-ul de acolo și poarta umană §10.5). Același SRT alimentează
    // redarea sincronă din hero și editorul preîncărcat.
    const demoAudioUrl = DEMO_AUDIO_URL;
    const demoAudio = ref(null);
    const demoCues = ref([]);
    const demoSrtText = ref('');
    const demoLoadFailed = ref(false);
    const demoOpening = ref(false);
    const isPlaying = ref(false);
    const playhead = ref(0);
    const playheadSeconds = ref(0);
    const demoDurationSeconds = ref(0);

    const activeCueText = computed(() => activeDemoCue(demoCues.value, playheadSeconds.value)?.text || '');

    const loadDemoTranscript = async () => {
      if (demoSrtText.value) return demoSrtText.value;
      const response = await fetch(DEMO_SRT_URL);
      if (!response.ok) throw new Error('demo srt unavailable');
      demoSrtText.value = await response.text();
      demoCues.value = parseDemoSrt(demoSrtText.value);
      if (demoCues.value.length === 0) throw new Error('demo srt empty');
      return demoSrtText.value;
    };

    const onDemoTimeUpdate = () => {
      const audio = demoAudio.value;
      if (!audio) return;
      playheadSeconds.value = audio.currentTime || 0;
      playhead.value = audio.duration ? (audio.currentTime / audio.duration) * 100 : 0;
    };
    const onDemoLoadedMetadata = () => {
      demoDurationSeconds.value = Math.floor(demoAudio.value?.duration || 0);
      onDemoTimeUpdate();
    };
    const onDemoEnded = () => {
      isPlaying.value = false;
      playhead.value = 100;
    };

    const togglePlay = async () => {
      const audio = demoAudio.value;
      if (!audio) return;
      if (audio.paused) {
        if (audio.ended) audio.currentTime = 0;
        try { await audio.play(); } catch { /* autoplay policy / decode: caption state stays honest */ }
      } else {
        audio.pause();
      }
    };

    const openDemoInEditor = async () => {
      if (demoOpening.value) return;
      demoOpening.value = true;
      try {
        const srtText = await loadDemoTranscript();
        demoLoadFailed.value = false;
        demoAudio.value?.pause();
        if (await editor.openDemoTranscript(srtText, demoAudioUrl)) {
          trackEvent('demo_opened', { source: 'homepage' });
        }
      } catch {
        demoLoadFailed.value = true;
      } finally {
        demoOpening.value = false;
      }
    };

    const trackSignupStarted = (source) => trackEvent('signup_started', { source });

    const signupFromDemo = async () => {
      trackSignupStarted('demo_banner');
      await editor.closeSrtEditor();
      router.push('/login');
    };

    const waveBars = Array.from({ length: 56 }, () => 18 + Math.floor(Math.random() * 78));

    const formatTime = (s) => {
      const total = Math.max(0, Math.floor(Number(s) || 0));
      const m = String(Math.floor(total / 60)).padStart(2, '0');
      const sec = String(total % 60).padStart(2, '0');
      return `00:${m}:${sec}`;
    };

    // ---- Animated stat counters ----
    const statTargets = [
      { value: 96, suffix: '%', label: 'hp.stats.accuracy', animated: ref(0) },
      { value: 40, suffix: '+', label: 'hp.stats.languages', animated: ref(0) },
      { value: 3, suffix: 'min', label: 'hp.stats.speed', animated: ref(0) },
      { value: 10, suffix: 'k+', label: 'hp.stats.volume', animated: ref(0) },
    ];
    const stats = statTargets;
    const animateStats = () => {
      statTargets.forEach((stat) => {
        let current = 0;
        const step = Math.max(1, Math.ceil(stat.value / 40));
        const t = setInterval(() => {
          current += step;
          if (current >= stat.value) { current = stat.value; clearInterval(t); }
          stat.animated.value = current;
        }, 30);
      });
    };

    // ---- Static content ----
    const marqueeLangs = [
      { name: 'Română', code: 'RO' }, { name: 'English', code: 'EN' }, { name: 'Deutsch', code: 'DE' },
      { name: 'Français', code: 'FR' }, { name: 'Español', code: 'ES' }, { name: 'Italiano', code: 'IT' },
      { name: 'العربية', code: 'AR' }, { name: '中文', code: 'ZH' }, { name: 'Magyar', code: 'HU' },
      { name: 'Polski', code: 'PL' }, { name: 'Português', code: 'PT' }, { name: '日本語', code: 'JA' },
    ];

    const steps = [
      { icon: 'fas fa-cloud-upload-alt', title: 'hp.steps.upload.t', desc: 'hp.steps.upload.d' },
      { icon: 'fas fa-microchip', title: 'hp.steps.process.t', desc: 'hp.steps.process.d' },
      { icon: 'fas fa-pen-nib', title: 'hp.steps.edit.t', desc: 'hp.steps.edit.d' },
      { icon: 'fas fa-download', title: 'hp.steps.export.t', desc: 'hp.steps.export.d' },
    ];

    onMounted(() => {
      loadDemoTranscript().catch(() => { demoLoadFailed.value = true; });
      animateStats();
    });
    onUnmounted(() => {
      demoAudio.value?.pause();
    });

    return {
      authStore, editor, currentMode, isDarkMode, dashboardPath, everydayPath, proPath,
      demoAudio, demoAudioUrl, demoLoadFailed, demoOpening, activeCueText,
      isPlaying, playhead, playheadSeconds, demoDurationSeconds,
      onDemoTimeUpdate, onDemoLoadedMetadata, onDemoEnded,
      openDemoInEditor, trackSignupStarted, signupFromDemo,
      waveBars, togglePlay, formatTime,
      stats, marqueeLangs, steps,
    };
  },
};
</script>

<style scoped>
/* Canonical tokens — mapped to --adm-* / --bg-* / --text-* with fallbacks (visual appearance preserved) */
.hp-root {
  --ink: var(--adm-text-strong, #0a0f2c);
  --ink-2: var(--adm-text-strong, #131a3d);
  --brand: var(--adm-accent, #003cff);
  --brand-soft: var(--adm-accent-soft, #e6ecff);
  --signal: var(--adm-success, #00e5a0);
  --amber: var(--adm-warning, #ffb800);
  --paper: var(--adm-bg, #f5f7fc);
  --body-text: var(--adm-text, #3a4160);
  --display-font: var(--adm-font-display, 'Sora', sans-serif);
  --body-font: var(--font-body, 'IBM Plex Sans', sans-serif);
  --mono-font: var(--adm-font-mono, 'IBM Plex Mono', monospace);
  font-family: var(--body-font);
  color: var(--body-text);
  background: var(--paper);
  min-height: 100vh;
  overflow-x: hidden;
}
.hp-root.dark-mode,
html.dark-mode .hp-root {
  --paper: var(--adm-bg, #0a0f2c);
  --body-text: var(--adm-text, #c3c9e2);
  --brand-soft: var(--adm-accent-soft, #1a2250);
}
.mono { font-family: var(--mono-font); }

/* ---------- Buttons ---------- */
.hp-btn {
  display: inline-flex; align-items: center; gap: 10px;
  font-family: var(--display-font); font-weight: 600;
  border: none; border-radius: 8px; cursor: pointer;
  text-decoration: none; transition: transform 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;
  padding: 11px 22px; font-size: 0.95rem;
}
.hp-btn-lg { padding: 15px 30px; font-size: 1.05rem; }
.hp-btn-primary { background: var(--brand); color: #fff; box-shadow: 0 6px 20px rgba(0,60,255,0.28); }
.hp-btn-primary:hover { transform: translateY(-2px); box-shadow: 0 10px 28px rgba(0,60,255,0.4); }
.hp-btn-ghost { background: transparent; color: #fff; border: 1.5px solid rgba(255,255,255,0.35); }
.hp-btn-ghost:hover { border-color: var(--signal); color: var(--signal); transform: translateY(-2px); }
.hp-btn-light { background: #fff; color: var(--ink); }
.hp-btn-light:hover { transform: translateY(-2px); box-shadow: 0 10px 26px rgba(0,0,0,0.25); }

/* ---------- Opening console ---------- */
.console-section {
  background: radial-gradient(1100px 600px at 78% 12%, #1c2554 0%, var(--ink) 55%);
  padding: 90px 6vw 110px;
  position: relative;
  overflow: hidden;
}
.console-section::before {
  content: ''; position: absolute; inset: 0;
  background-image: linear-gradient(rgba(255,255,255,0.035) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255,255,255,0.035) 1px, transparent 1px);
  background-size: 44px 44px;
  pointer-events: none;
}
.console-grid {
  display: grid; grid-template-columns: 1fr 1.1fr; gap: 60px;
  align-items: center; max-width: 1240px; margin: 0 auto; position: relative;
}
.live-chip {
  display: inline-flex; align-items: center; gap: 8px;
  background: rgba(0,229,160,0.12); border: 1px solid rgba(0,229,160,0.4);
  color: var(--signal); border-radius: 999px; padding: 6px 14px;
  font-size: 0.75rem; letter-spacing: 0.12em; text-transform: uppercase; margin-bottom: 26px;
}
.live-dot { width: 8px; height: 8px; border-radius: 50%; background: var(--signal); animation: pulseDot 1.4s infinite; }
@keyframes pulseDot { 0%,100% { box-shadow: 0 0 0 0 rgba(0,229,160,0.5);} 60% { box-shadow: 0 0 0 8px rgba(0,229,160,0);} }
.console-headline {
  font-family: var(--display-font); font-weight: 800;
  font-size: clamp(2.4rem, 4.6vw, 3.9rem); line-height: 1.06;
  color: #fff; margin: 0 0 22px; letter-spacing: -0.02em;
}
.headline-accent {
  color: var(--signal); position: relative; white-space: nowrap;
}
.headline-accent::after {
  content: ''; position: absolute; left: 0; bottom: 4px; height: 10px; width: 100%;
  background: rgba(0,229,160,0.22); z-index: -1; border-radius: 3px;
}
.console-sub { color: #aeb6d8; font-size: 1.12rem; line-height: 1.65; max-width: 46ch; margin: 0 0 34px; }
.console-ctas { display: flex; gap: 14px; flex-wrap: wrap; margin-bottom: 30px; }
.console-meta { color: #6b7399; font-size: 0.78rem; letter-spacing: 0.06em; }

/* Console window */
.console-window {
  background: var(--ink-2); border: 1px solid rgba(255,255,255,0.1);
  border-radius: 14px; overflow: hidden;
  box-shadow: 0 30px 70px rgba(0,0,0,0.5);
  transform: rotate(0.6deg);
  transition: transform 0.3s ease;
}
.console-window:hover { transform: rotate(0deg) translateY(-4px); }
.console-titlebar {
  display: flex; align-items: center; gap: 8px;
  padding: 12px 16px; background: rgba(255,255,255,0.04);
  border-bottom: 1px solid rgba(255,255,255,0.08);
}
.tb-dot { width: 11px; height: 11px; border-radius: 50%; }
.tb-dot.red { background: #ff5f57; } .tb-dot.amber { background: #febc2e; } .tb-dot.green { background: #28c840; }
.tb-file { color: #8a92b8; font-size: 0.78rem; margin-left: 8px; }
.tb-rec { margin-left: auto; color: #ff5f57; font-size: 0.72rem; letter-spacing: 0.15em; display: flex; align-items: center; gap: 6px; }
.rec-dot { width: 7px; height: 7px; border-radius: 50%; background: #ff5f57; animation: pulseDot 1s infinite; }

.waveform {
  display: flex; align-items: center; gap: 3px; height: 110px;
  padding: 0 20px; cursor: pointer; position: relative;
}
.wave-bar {
  flex: 1; min-width: 3px; border-radius: 2px;
  background: linear-gradient(180deg, var(--signal), #00b3ff);
  opacity: 0.55; transition: opacity 0.2s;
}
.wave-bar.playing { animation: wavePulse 0.9s ease-in-out infinite alternate; }
@keyframes wavePulse { from { transform: scaleY(0.55); opacity: 0.45; } to { transform: scaleY(1); opacity: 0.95; } }
.wave-progress {
  position: absolute; top: 0; bottom: 0; left: 0;
  background: rgba(0,229,160,0.08); border-right: 2px solid var(--signal);
  pointer-events: none;
}
.timecode-row {
  display: flex; justify-content: space-between; align-items: center;
  padding: 0 20px 14px; color: #6b7399; font-size: 0.75rem;
}
.tc-lang { color: var(--signal); border: 1px solid rgba(0,229,160,0.4); padding: 2px 8px; border-radius: 4px; }

.caption-box { padding: 18px 20px; background: rgba(0,0,0,0.28); min-height: 96px; }
.caption-label { color: #6b7399; font-size: 0.7rem; letter-spacing: 0.15em; text-transform: uppercase; margin-bottom: 8px; }
.caption-text {
  color: #fff; font-size: 1.05rem; line-height: 1.55; margin: 0;
  font-family: var(--body-font); min-height: 3.1em;
}
.caret { display: inline-block; width: 2px; height: 1.15em; background: var(--signal); vertical-align: text-bottom; margin-left: 2px; }
.caret.blink { animation: blink 0.9s step-end infinite; }
@keyframes blink { 50% { opacity: 0; } }

.lang-chips { display: flex; gap: 8px; padding: 14px 20px; flex-wrap: wrap; }
.lang-chip {
  background: transparent; border: 1px solid rgba(255,255,255,0.2); color: #aeb6d8;
  padding: 5px 13px; border-radius: 6px; font-size: 0.75rem; cursor: pointer;
  text-transform: uppercase; transition: all 0.18s;
}
.lang-chip:hover { border-color: var(--signal); color: var(--signal); }
.lang-chip.active { background: var(--signal); border-color: var(--signal); color: var(--ink); font-weight: 600; }

/* ---------- Marquee ---------- */
.marquee-section { background: var(--brand); overflow: hidden; padding: 16px 0; }
.marquee-track {
  display: flex; gap: 48px; width: max-content;
  animation: marquee 30s linear infinite;
}
.marquee-section:hover .marquee-track { animation-play-state: paused; }
@keyframes marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }
.marquee-item { display: flex; align-items: baseline; gap: 10px; white-space: nowrap; }
.marquee-name { color: #fff; font-family: var(--display-font); font-weight: 600; font-size: 1.05rem; }
.marquee-code { color: rgba(255,255,255,0.55); font-size: 0.75rem; }

/* ---------- Section heads ---------- */
.section-head { max-width: 1240px; margin: 0 auto 56px; padding: 0 6vw; }
.section-kicker {
  color: var(--brand); font-size: 0.78rem; letter-spacing: 0.2em;
  text-transform: uppercase; display: block; margin-bottom: 12px;
}
.dark-mode .section-kicker,
html.dark-mode .section-kicker { color: var(--signal); }
.section-title {
  font-family: var(--display-font); font-weight: 800; letter-spacing: -0.02em;
  font-size: clamp(1.9rem, 3.4vw, 2.8rem); color: var(--ink); margin: 0;
}
.dark-mode .section-title,
html.dark-mode .section-title { color: #fff; }

/* ---------- Steps timeline ---------- */
.steps-section { padding: 110px 0 90px; }
.steps-timeline { max-width: 900px; margin: 0 auto; padding: 0 6vw; position: relative; }
.steps-timeline::before {
  content: ''; position: absolute; left: 50%; top: 0; bottom: 0; width: 2px;
  background: linear-gradient(180deg, var(--brand), var(--signal)); opacity: 0.25;
}
.step-row {
  display: flex; align-items: flex-start; gap: 28px; margin-bottom: 52px;
  position: relative; transition: transform 0.25s ease;
}
.step-row:hover { transform: translateX(6px); }
.step-row.step-flip { flex-direction: row-reverse; text-align: right; }
.step-row.step-flip:hover { transform: translateX(-6px); }
.step-num {
  font-size: 2.6rem; font-weight: 500; color: var(--brand); opacity: 0.35;
  line-height: 1; flex-shrink: 0; width: 70px;
}
.step-body { flex: 1; }
.step-icon {
  width: 52px; height: 52px; border-radius: 12px; display: inline-flex;
  align-items: center; justify-content: center; font-size: 1.3rem;
  background: var(--brand-soft); color: var(--brand); margin-bottom: 14px;
  transition: transform 0.2s;
}
.step-row:hover .step-icon { transform: scale(1.1) rotate(-4deg); }
.step-title { font-family: var(--display-font); font-weight: 700; font-size: 1.25rem; color: var(--ink); margin: 0 0 8px; }
.dark-mode .step-title,
html.dark-mode .step-title { color: #fff; }
.step-desc { margin: 0; line-height: 1.6; max-width: 44ch; }
.step-row.step-flip .step-desc { margin-left: auto; }

/* ---------- Stats ---------- */
.stats-section { background: var(--ink); padding: 80px 6vw; }
.stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 40px; max-width: 1240px; margin: 0 auto; }
.stat-block { text-align: left; border-left: 3px solid var(--signal); padding-left: 22px; }
.stat-number { font-family: var(--display-font); font-weight: 800; color: #fff; line-height: 1; }
.stat-value { font-size: clamp(2.6rem, 4.5vw, 3.8rem); }
.stat-suffix { font-size: 1.4rem; color: var(--signal); margin-left: 4px; }
.stat-label { color: #8a92b8; font-size: 0.9rem; margin-top: 10px; line-height: 1.4; }

/* ---------- Capabilities ---------- */
.caps-section { padding: 110px 0; }
.caps-grid {
  display: grid; grid-template-columns: repeat(3, 1fr); gap: 22px;
  max-width: 1240px; margin: 0 auto; padding: 0 6vw;
}
.cap-card {
  background: #fff; border: 1px solid #e4e8f5; border-radius: 12px;
  padding: 30px 28px; transition: transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s;
  position: relative; overflow: hidden;
}
.dark-mode .cap-card,
html.dark-mode .cap-card { background: var(--ink-2); border-color: #232b57; }
.cap-card::after {
  content: ''; position: absolute; top: 0; left: 0; width: 0; height: 3px;
  background: var(--brand); transition: width 0.3s ease;
}
.cap-card:hover { transform: translateY(-6px); box-shadow: 0 18px 40px rgba(10,15,44,0.12); border-color: var(--brand); }
.cap-card:hover::after { width: 100%; }
.cap-wide { grid-column: span 2; }
.cap-icon { font-size: 1.6rem; color: var(--brand); margin-bottom: 16px; }
.cap-card h3 { font-family: var(--display-font); font-weight: 700; font-size: 1.15rem; color: var(--ink); margin: 0 0 10px; }
.dark-mode .cap-card h3,
html.dark-mode .cap-card h3 { color: #fff; }
.cap-card p { margin: 0; line-height: 1.6; font-size: 0.95rem; }
.cap-tags { display: flex; gap: 8px; margin-top: 18px; }
.cap-tags span {
  background: var(--brand-soft); color: var(--brand); padding: 4px 10px;
  border-radius: 5px; font-size: 0.72rem; letter-spacing: 0.05em;
}

/* ---------- Pricing teaser ---------- */
.pricing-teaser { padding: 0 6vw 110px; }
.teaser-inner {
  max-width: 1240px; margin: 0 auto; background: var(--brand);
  border-radius: 16px; padding: 56px 60px;
  display: flex; align-items: center; justify-content: space-between; gap: 30px;
  position: relative; overflow: hidden;
}
.teaser-inner::before {
  content: ''; position: absolute; right: -80px; top: -80px; width: 300px; height: 300px;
  border: 40px solid rgba(255,255,255,0.08); border-radius: 50%;
}
.teaser-copy h2 { font-family: var(--display-font); font-weight: 800; color: #fff; font-size: clamp(1.6rem, 3vw, 2.3rem); margin: 0 0 10px; }
.teaser-copy p { color: rgba(255,255,255,0.8); margin: 0; font-size: 1.05rem; }

/* ---------- Footer CTA ---------- */
.footer-cta { background: var(--ink); text-align: center; padding: 110px 6vw 130px; }
.footer-headline {
  font-family: var(--display-font); font-weight: 800; color: #fff;
  font-size: clamp(2rem, 4vw, 3.2rem); letter-spacing: -0.02em; margin: 0 0 34px;
}
.footer-note { color: #6b7399; font-size: 0.78rem; margin-top: 26px; letter-spacing: 0.05em; }
.footer-guides { margin-top: 14px; }
.footer-guides-link { color: #aab3da; font-size: 0.9rem; text-decoration: underline; text-underline-offset: 3px; }
.footer-guides-link:hover { color: #ffffff; }

/* ---------- Radio gate ---------- */
.radio-gate { text-align: center; padding: 140px 6vw; }
.radio-title { font-family: var(--display-font); font-weight: 800; color: var(--ink); font-size: 2.6rem; }
.dark-mode .radio-title,
html.dark-mode .radio-title { color: #fff; }
.radio-sub { color: var(--body-text); margin: 14px 0 30px; }

/* ---------- Two doors (everyday vs pro) ---------- */
.two-doors {
  display: grid; grid-template-columns: 1fr 1fr; gap: 14px;
  margin-bottom: 22px;
}
.door-card {
  display: flex; align-items: center; gap: 14px;
  background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.18);
  border-radius: 12px; padding: 16px 16px;
  text-decoration: none; color: #fff;
  transition: transform 0.18s ease, border-color 0.18s ease, background 0.18s ease, box-shadow 0.18s ease;
}
.door-card:hover { transform: translateY(-2px); border-color: var(--signal); background: rgba(255,255,255,0.12); box-shadow: 0 10px 28px rgba(0,0,0,0.25); }
.door-icon {
  width: 42px; height: 42px; border-radius: 10px; flex-shrink: 0;
  display: inline-flex; align-items: center; justify-content: center; font-size: 1.05rem;
  background: rgba(255,255,255,0.12); color: var(--signal); border: 1px solid rgba(255,255,255,0.18);
}
.door-body { flex: 1; display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.door-title { font-family: var(--display-font); font-weight: 700; font-size: 0.98rem; color: #fff; line-height: 1.2; }
.door-desc { font-size: 0.82rem; color: #aeb6d8; line-height: 1.35; }
.door-arrow { color: rgba(255,255,255,0.55); font-size: 0.85rem; flex-shrink: 0; }
.door-card:hover .door-arrow { color: var(--signal); }
@media (max-width: 720px) { .two-doors { grid-template-columns: 1fr; } }

/* ---------- Responsive ---------- */
@media (max-width: 980px) {
  .console-grid { grid-template-columns: 1fr; gap: 44px; }
  .console-window { transform: none; }
  .caps-grid { grid-template-columns: 1fr; }
  .cap-wide { grid-column: span 1; }
  .stats-grid { grid-template-columns: 1fr 1fr; gap: 34px; }
  .steps-timeline::before { display: none; }
  .step-row, .step-row.step-flip { flex-direction: column; text-align: left; gap: 12px; }
  .step-row.step-flip .step-desc { margin-left: 0; }
  .teaser-inner { flex-direction: column; text-align: center; padding: 44px 30px; }
}
/* 390px phones: stats become a single column so numbers never squeeze */
@media (max-width: 480px) {
  .stats-grid { grid-template-columns: 1fr; gap: 26px; }
  .caps-section { padding: 72px 0; }
}

/* ---------- Demo fără cont (Pas 1.2) ---------- */
.demo-banner {
  position: sticky; top: 0; z-index: 1200;
  display: flex; align-items: center; justify-content: center; gap: 16px; flex-wrap: wrap;
  background: var(--ink-2); color: #e6e9f7;
  padding: 10px 18px; font-size: 0.9rem;
  border-bottom: 2px solid var(--signal);
}
.demo-banner-cta {
  background: var(--brand); color: #fff; border: none; border-radius: 8px;
  padding: 7px 16px; font-family: var(--display-font); font-weight: 600; cursor: pointer;
}
.demo-asset-note { color: #6b7399; font-size: 0.7rem; margin-top: 10px; }
</style>
